import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Download, Loader2, Star } from "lucide-react";
import api from "@/lib/api";
import Logo from "@/components/landing/Logo";
import { CardPay, FeeLine, SaveCardBox } from "@/components/CardPay";
import { toast } from "sonner";
import { TipPicker } from "@/components/TipPicker";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function InvoicePage() {
  const { invoiceId } = useParams();
  const [params] = useSearchParams();
  const t = params.get("t") || "";
  const [inv, setInv] = useState(null);
  const [err, setErr] = useState("");
  const [tip, setTip] = useState(0);
  const [paying, setPaying] = useState(false);
  const [save, setSave] = useState(true);
  const pay = async (sourceId, verificationToken) => {
    setPaying(true);
    try {
      await api.post(`/invoice/${invoiceId}/pay`, { t, source_id: sourceId, verification_token: verificationToken, tip, save_card: save });
      setInv({ ...inv, status: "paid" });
      toast.success("Paid — thank you!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payment failed");
    } finally {
      setPaying(false);
    }
  };
  useEffect(() => {
    api.get(`/invoice/${invoiceId}`, { params: { t } }).then((r) => setInv(r.data)).catch(() => setErr("Invoice not found."));
  }, [invoiceId, t]);

  if (err) return <div className="min-h-screen bg-[#0A0A0A] text-red-300 flex items-center justify-center" data-testid="invoice-error">{err}</div>;
  if (!inv) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-sky-400" /></div>;
  const b = inv.business;
  const paid = inv.status === "paid";
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white p-4 sm:p-8" data-testid="invoice-page">
      <div className="max-w-2xl mx-auto rounded-3xl border border-white/10 bg-[#0d0d0d] overflow-hidden">
        <div className="p-6 sm:p-8 flex flex-col sm:flex-row justify-between gap-4 border-b border-white/5">
          <Logo size={48} showWordmark />
          <div className="sm:text-right text-sm text-gray-400">
            <p className="text-2xl font-['Outfit'] text-white">Invoice {inv.number}</p>
            <p>{inv.service_date}</p>
            <p>{b.phone} · {b.email}</p>
            <p>{b.website} · {b.address}</p>
          </div>
        </div>
        <div className="p-6 sm:p-8 space-y-6">
          <div className="flex justify-between gap-4 flex-wrap">
            <div className="text-sm">
              <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">Bill to</p>
              <p className="text-white">{inv.customer_name}</p>
              {[inv.customer_phone, inv.customer_email, inv.customer_address, inv.vehicle && `Vehicle: ${inv.vehicle}`].filter(Boolean).map((l) => <p key={l} className="text-gray-400">{l}</p>)}
            </div>
            <span className={`h-fit px-3 py-1 rounded-full text-xs uppercase tracking-widest ${paid ? "bg-emerald-500/15 text-emerald-300" : "bg-sky-500/15 text-sky-300"}`} data-testid="invoice-status">{paid ? "Paid" : "Balance due"}</span>
          </div>
          {(inv.customer_company || inv.end_customer) && (
            <p className="text-sm text-gray-400" data-testid="invoice-billto-extra">{inv.customer_company && <>Billed to <span className="text-white">{inv.customer_company}</span></>}{inv.end_customer && <> · Vehicle owner: <span className="text-white">{inv.end_customer}</span></>}</p>
          )}
          <div className="rounded-2xl border border-white/10 divide-y divide-white/5">
            {inv.items.map((it, i) => (
              <div key={i} className="flex justify-between gap-4 p-3 text-sm">
                <span>{it.description} {it.qty !== 1 && <span className="text-gray-500">× {it.qty}</span>}</span>
                <span>{money(it.qty * it.price)}</span>
              </div>
            ))}
          </div>
          <div className="ml-auto max-w-xs text-sm space-y-1">
            <div className="flex justify-between text-gray-400"><span>Subtotal</span><span>{money(inv.subtotal)}</span></div>
            <FeeLine fee={inv.card_fee || 0} pct={inv.card_fee_pct} />
            {inv.discount > 0 && <div className="flex justify-between text-emerald-300" data-testid="invoice-discount"><span>{inv.discount_label || "Discount"} ({inv.discount_pct}%)</span><span>-{money(inv.discount)}</span></div>}
            <div className="flex justify-between text-gray-400"><span>Tax ({inv.tax_rate}%)</span><span>{money(inv.tax)}</span></div>
            <div className="flex justify-between"><span>Total</span><span>{money(inv.total)}</span></div>
            <div className="flex justify-between text-gray-400"><span>Deposit paid</span><span>-{money(inv.deposit_paid)}</span></div>
            <div className="flex justify-between text-lg font-semibold text-sky-400 pt-2 border-t border-white/10"><span>{paid ? "Paid in full" : "Balance due"}</span><span data-testid="invoice-balance">{money(paid ? 0 : inv.balance_due)}</span></div>
          </div>
          {inv.notes && <p className="text-sm text-gray-400 italic">Notes: {inv.notes}</p>}
          {!paid && inv.balance_due > 0 && inv.processor?.configured && (
            <div className="rounded-2xl border border-sky-500/20 p-4 space-y-4" data-testid="invoice-pay-section">
              <p className="text-white font-['Outfit'] text-lg">Pay balance</p>
              <TipPicker base={Number(inv.total)} tip={tip} setTip={setTip} testid="invoice-tip" />
              <CardPay amount={inv.balance_due + tip} onToken={pay} busy={paying} label={`Pay $${(inv.balance_due + tip).toFixed(2)}`} buyerName={inv.customer_name} />
              <SaveCardBox checked={save} onChange={setSave} />
            </div>
          )}
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/invoice/${invoiceId}/pdf?t=${t}`} target="_blank" rel="noreferrer" data-testid="invoice-pdf" className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-sky-500 hover:bg-sky-400 text-white text-sm font-medium">
            <Download className="w-4 h-4" /> Download PDF
          </a>
          {inv.review_link && (
            <a href={inv.review_link} target="_blank" rel="noreferrer" data-testid="invoice-google-review" className="ml-3 inline-flex items-center gap-2 h-11 px-5 rounded-full border border-sky-500/40 text-sky-300 hover:bg-sky-500/10 text-sm font-medium">
              <Star className="w-4 h-4" /> Leave a Google review
            </a>
          )}
          <div className="text-[11px] text-gray-500 whitespace-pre-line border-t border-white/5 pt-4" data-testid="invoice-terms">{inv.terms}</div>
        </div>
      </div>
    </div>
  );
}
